import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Segment } from '../models/Segment';

/**
 * Interface for storing segment state for undo operations
 */
interface SegmentSpeakerState {
  id: string;
  speakerLabel: string;
}

/**
 * Interface for storing the state of multiple segments for undo operations
 */
interface MultiSegmentSpeakerState {
  segmentStates: {
    id: string;
    speakerLabel: string;
  }[];
}

/**
 * Command for assigning a speaker to a complete segment including all underlying elements
 * 
 * This command updates the speaker label for a segment and all its contained text elements.
 * It can work with either anonymous speaker labels, Dato Speaker IDs, or both.
 */
export class AssignSpeakerToSegmentCommand extends AbstractTranscriptEditorCommand {
  /** The segment to update */
  private segment: Segment;

  /** Speaker label to assign (spk_X format) */
  private speakerLabel: string | null;

  /** Dato CMS ID of the speaker */
  private datoSpeakerId: string | null;

  /** Original speaker label for undo operations */
  private previousSpeakerLabel: string;

  /** ID of the user making the change */
  private userId: string;

  /** Complete state for undo (single segment) */
  private undoState: SegmentSpeakerState;

  /** Multi-segment undo state for updating all segments with the same speaker */
  private multiSegmentUndoState: MultiSegmentSpeakerState | null = null;

  /** The effective speaker label used after execution */
  private effectiveSpeakerLabel: string | null;

  /** The segments that were updated during execution (for multi-segment updates) */
  private updatedSegments: Segment[] = [];
  
  /**
   * Creates a new AssignSpeakerToSegmentCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segment The segment to change speaker for
   * @param speakerLabel Anonymous speaker label to assign (spk_X format)
   * @param datoSpeakerId Dato CMS ID of the speaker
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segment: Segment,
    speakerLabel: string | null,
    datoSpeakerId: string | null,
    userId: string
  ) {
    // Create command description based on available information
    let description = '';
    if (speakerLabel && datoSpeakerId) {
      description = `Assign speaker ${speakerLabel.slice(0, 8)} to segment ${segment.id.slice(0, 8)}`;
    } else if (speakerLabel) {
      description = `Assign anonymous speaker ${speakerLabel.slice(0, 8)} to segment ${segment.id.slice(0, 8)}`;
    } else if (datoSpeakerId) {
      description = `Assign dato speaker ${datoSpeakerId.slice(0, 8)} to segment ${segment.id.slice(0, 8)}`;
    }
    
    super(description, editor);
    
    this.segment = segment;
    this.speakerLabel = speakerLabel;
    this.datoSpeakerId = datoSpeakerId;
    this.userId = userId;
    this.effectiveSpeakerLabel = speakerLabel; // Initial value, may change during execution
    
    // Optimization: Speaker assignments don't need sorting or heavy post-execution hooks
    this.setRequiresSorting(false);
    this.setNeedsHeavyOptimization(true);
    
    // Remember the old speaker label for undo
    this.previousSpeakerLabel = segment.speakerLabel;

    // Store segment state for undo
    this.undoState = {
      id: segment.id,
      speakerLabel: segment.speakerLabel
    };
    
    // Add metadata for debugging and traceability
    this.setMetadata('segmentId', segment.id);
    this.setMetadata('initialSpeakerLabel', this.previousSpeakerLabel);
    this.setMetadata('newSpeakerLabel', speakerLabel);
    this.setMetadata('datoSpeakerId', datoSpeakerId);
    this.setMetadata('userId', userId);
  }
  
  /**
   * Execute the command
   *
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Use the editor's batch method for proper batching
      let success = false;

      await this.editor.withBatch(async () => {
        try {
          // Handle different scenarios based on what is provided

          // SCENARIO 1: Only speaker_label is provided
          if (this.speakerLabel && !this.datoSpeakerId) {
            // Update segment and all text elements - single segment only
            this.segment.speakerLabel = this.speakerLabel;
            this.updateAllTextElements(this.speakerLabel);
            this.effectiveSpeakerLabel = this.speakerLabel;

            // Notify about speaker assignments changing - this refreshes the speakers panel
            this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
              segmentId: this.segment.id,
              newLabel: this.effectiveSpeakerLabel,
              oldLabel: this.previousSpeakerLabel
            });
            
          }

          // SCENARIO 2: Only datoSpeakerId is provided
          else if (!this.speakerLabel && this.datoSpeakerId) {
            // Check if this datoSpeakerId is already in the speaker mapping
            const existingSpeakerLabel = this.findSpeakerLabelForDatoId(this.datoSpeakerId);
            const existingDatoId = this.findDatoIdForSpeakerLabel(this.previousSpeakerLabel);

            if (existingSpeakerLabel) {
              // Update the segment and all elements with the existing speaker label - single segment only
              this.segment.speakerLabel = existingSpeakerLabel;
              this.updateAllTextElements(existingSpeakerLabel);
              this.effectiveSpeakerLabel = existingSpeakerLabel;
            } else if (existingDatoId) {
              // then create new anonymous speaker label and update the segment and all elements
              const newSpeakerLabel = `spk_${this.editor.nextSpeakerNumber()}`;
              this.segment.speakerLabel = newSpeakerLabel;
              this.updateAllTextElements(newSpeakerLabel);
              this.effectiveSpeakerLabel = newSpeakerLabel;
              this.editor.updateSpeakerMapping(newSpeakerLabel, this.datoSpeakerId);
            } else {
              // Just update the speaker mapping for the current speaker label
              this.editor.updateSpeakerMapping(this.segment.speakerLabel, this.datoSpeakerId);
              this.effectiveSpeakerLabel = this.segment.speakerLabel;
            }

            // Notify about speaker assignments changing - this refreshes the speakers panel
            this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
              segmentId: this.segment.id,
              newLabel: this.effectiveSpeakerLabel,
              oldLabel: this.previousSpeakerLabel
            });
          }

          // SCENARIO 3: Both are provided - this is where we'll update all segments with the same label
          else if (this.speakerLabel && this.datoSpeakerId) {
            // Check if this datoSpeakerId is already mapped to a speaker
            const existingSpeakerLabel = this.findSpeakerLabelForDatoId(this.datoSpeakerId);
            const existingDatoId = this.findDatoIdForSpeakerLabel(this.previousSpeakerLabel);
            if (existingSpeakerLabel  && !existingDatoId) {
              // A mapping already exists for the new datoSpeakerId but not for the previous speaker label
              // Capture multi-segment undo state before making changes
              this.captureMultiSegmentUndoState();

              // Update all segments with the previous label to use the existing speaker label
              this.updateAllSegmentsWithLabel(this.previousSpeakerLabel, existingSpeakerLabel);
              this.effectiveSpeakerLabel = existingSpeakerLabel;

              // Notify about speaker mappings changing - this refreshes the speakers panel
              this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
                segmentId: this.segment.id,
                newLabel: this.effectiveSpeakerLabel,
                oldLabel: this.previousSpeakerLabel
              });
            } else {
              // Create a new mapping - update the current segment only
              this.segment.speakerLabel = this.speakerLabel;
              this.updateAllTextElements(this.speakerLabel);

              // Update speaker mapping
              this.editor.updateSpeakerMapping(this.speakerLabel, this.datoSpeakerId);
              this.effectiveSpeakerLabel = this.speakerLabel;

              // Notify about speaker assignments changing - this refreshes the speakers panel
              this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
                segmentId: this.segment.id,
                newLabel: this.effectiveSpeakerLabel,
                oldLabel: this.previousSpeakerLabel
              });
            }
          }

          // Save the effective speaker label for undo/redo
          this.setMetadata('effectiveSpeakerLabel', this.effectiveSpeakerLabel);

          // If we updated multiple segments, notify about each one
          if (this.multiSegmentUndoState && this.updatedSegments.length > 0) {
            // Notify for each segment that was updated
            for (const segment of this.updatedSegments) {
              this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
                segmentId: segment.id,
                newLabel: this.effectiveSpeakerLabel,
                oldLabel: this.previousSpeakerLabel
              });
            }

            // Record a batched operation - using the standard speaker_assignment type
            this.editor.recordOperation({
              type: 'speaker_assignment',
              segmentId: this.segment.id, // Primary segment
              data: {
                oldSpeakerLabel: this.previousSpeakerLabel,
                newSpeakerLabel: this.effectiveSpeakerLabel,
                datoSpeakerId: this.datoSpeakerId,
                scope: 'multi_segment',
                segmentIds: this.updatedSegments.map(s => s.id)
              },
              userId: this.userId,
              timestamp: Date.now()
            });
          } else {
            // Single segment case - just notify for the one segment
            this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
              segmentId: this.segment.id,
              newLabel: this.effectiveSpeakerLabel,
              oldLabel: this.previousSpeakerLabel
            });

            // Record the operation for a single segment
            this.editor.recordOperation({
              type: 'speaker_assignment',
              segmentId: this.segment.id,
              data: {
                oldSpeakerLabel: this.previousSpeakerLabel,
                newSpeakerLabel: this.effectiveSpeakerLabel,
                datoSpeakerId: this.datoSpeakerId,
                scope: 'segment'
              },
              userId: this.userId,
              timestamp: Date.now()
            });
          }

          success = true;
        } catch (error) {
          console.error('Error in AssignSpeakerToSegmentCommand.execute:', error);
          success = false;
        }
      });

      return success;
    } catch (error) {
      console.error('Error executing AssignSpeakerToSegmentCommand:', error);
      return false;
    }
  }
  
  /**
   * Undo the command
   *
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    try {
      // Use the editor's batch method for proper batching
      let success = false;

      await this.editor.withBatch(async () => {
        try {
          // Handle different scenarios based on what was provided during execute
          
          // SCENARIO 1: Only speaker_label was provided
          if (this.speakerLabel && !this.datoSpeakerId) {
            // Restore the previous speaker label on the segment
            this.segment.speakerLabel = this.previousSpeakerLabel;
            // Restore the previous speaker label on all text elements
            this.updateAllTextElements(this.previousSpeakerLabel);
            
            // Notify about speaker assignments changing
            this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
              segmentId: this.segment.id,
              newLabel: this.previousSpeakerLabel,
              oldLabel: this.effectiveSpeakerLabel
            });
          }

          // SCENARIO 2: Only datoSpeakerId was provided
          else if (!this.speakerLabel && this.datoSpeakerId) {
            // Check what happened during execute
            const existingSpeakerLabel = this.findSpeakerLabelForDatoId(this.datoSpeakerId);
            const existingDatoId = this.findDatoIdForSpeakerLabel(this.previousSpeakerLabel);
            
            if (existingSpeakerLabel && this.effectiveSpeakerLabel === existingSpeakerLabel) {
              // We updated the segment with an existing speaker label
              this.segment.speakerLabel = this.previousSpeakerLabel;
              this.updateAllTextElements(this.previousSpeakerLabel);
            } else if (existingDatoId && this.effectiveSpeakerLabel !== this.previousSpeakerLabel) {
              // We created a new anonymous speaker label
              this.segment.speakerLabel = this.previousSpeakerLabel;
              this.updateAllTextElements(this.previousSpeakerLabel);
              // Remove the speaker mapping that was created
              this.editor.updateSpeakerMapping(this.effectiveSpeakerLabel!, '');
            } else {
              // We just updated the speaker mapping - need to revert it
              // Find what the previous mapping was for this speaker label
              const previousDatoId = this.findDatoIdForSpeakerLabel(this.previousSpeakerLabel);
              this.editor.updateSpeakerMapping(this.previousSpeakerLabel, previousDatoId || '');
            }
            
            // Notify about speaker assignments changing
            this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
              segmentId: this.segment.id,
              newLabel: this.previousSpeakerLabel,
              oldLabel: this.effectiveSpeakerLabel
            });
          }

          // SCENARIO 3: Both were provided
          else if (this.speakerLabel && this.datoSpeakerId) {
            // Check if we have a multi-segment undo state
            if (this.multiSegmentUndoState && this.multiSegmentUndoState.segmentStates.length > 0) {
              // Restore all segments to their original state
              this.restoreMultiSegmentState();

              // Notify for each segment that was updated
              for (const segmentState of this.multiSegmentUndoState.segmentStates) {
                this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
                  segmentId: segmentState.id,
                  newLabel: segmentState.speakerLabel,
                  oldLabel: this.effectiveSpeakerLabel
                });
              }
            } else {
              // Single segment case - restore just the one segment
              this.segment.speakerLabel = this.previousSpeakerLabel;
              this.updateAllTextElements(this.previousSpeakerLabel);
              
              // Revert the speaker mapping if needed
              const previousDatoId = this.findDatoIdForSpeakerLabel(this.speakerLabel);
              if (previousDatoId === this.datoSpeakerId) {
                // Remove the mapping that was created
                this.editor.updateSpeakerMapping(this.speakerLabel, '');
              }
              
              // Notify about speaker assignments changing
              this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
                segmentId: this.segment.id,
                newLabel: this.previousSpeakerLabel,
                oldLabel: this.effectiveSpeakerLabel
              });
            }
          }

          success = true;
        } catch (error) {
          console.error('Error in AssignSpeakerToSegmentCommand.undo:', error);
          success = false;
        }
      });

      return success;
    } catch (error) {
      console.error('Error undoing AssignSpeakerToSegmentCommand:', error);
      return false;
    }
  }
  
  /**
   * Captures the current state of all segments with the specified speaker label
   * for undo operations
   */
  private captureMultiSegmentUndoState(): void {
    const container = this.editor.getContainer();
    if (!container) return;

    const segments = this.getSegmentsWithLabel(this.previousSpeakerLabel);

    // Create an entry for each segment that will be updated
    this.multiSegmentUndoState = {
      segmentStates: segments.map(segment => ({
        id: segment.id,
        speakerLabel: segment.speakerLabel
      }))
    };

    // Also store the segments for later reference
    this.updatedSegments = [...segments];
  }

  /**
   * Restores all segments to their original state from the undo state
   */
  private restoreMultiSegmentState(): void {
    if (!this.multiSegmentUndoState) return;

    const container = this.editor.getContainer();
    if (!container) return;

    // For each saved segment state, find the segment and restore it
    for (const segmentState of this.multiSegmentUndoState.segmentStates) {
      const segment = container.segments.find(s => s.id === segmentState.id);
      if (segment) {
        // Restore the original speaker label
        segment.speakerLabel = segmentState.speakerLabel;

        // Also update all text elements
        segment.paragraphs.forEach(paragraph => {
          paragraph.elements.forEach(element => {
            // Update speaker label on all elements
            element.speakerLabel = segmentState.speakerLabel;
            // Ensure parent references are maintained for all elements
            element.segmentId = segment.id;
            element.paragraphId = paragraph.id;
          });
        });

        // After updating all elements, ensure all parent-child relationships are updated
        segment.updateInternalData();
      }
    }
  }

  /**
   * Gets all segments with a specific speaker label
   *
   * @param speakerLabel The speaker label to find
   * @returns Array of segments with the matching label
   */
  private getSegmentsWithLabel(speakerLabel: string): Segment[] {
    const container = this.editor.getContainer();
    if (!container) return [];

    return container.segments.filter(segment => segment.speakerLabel === speakerLabel);
  }

  /**
   * Updates all segments with a specific speaker label to have a new label
   *
   * @param fromSpeakerLabel The original speaker label to match
   * @param toSpeakerLabel The new speaker label to assign
   */
  private updateAllSegmentsWithLabel(fromSpeakerLabel: string, toSpeakerLabel: string): void {
    const segmentsToUpdate = this.getSegmentsWithLabel(fromSpeakerLabel);

    // Store the updated segments for notifications
    this.updatedSegments = [...segmentsToUpdate];

    // Update each segment and its elements
    segmentsToUpdate.forEach(segment => {
      segment.speakerLabel = toSpeakerLabel;

      // Update all text elements within the segment
      segment.paragraphs.forEach(paragraph => {
        paragraph.elements.forEach(element => {
          element.speakerLabel = toSpeakerLabel;
          // Ensure parent references are maintained
          element.segmentId = segment.id;
          element.paragraphId = paragraph.id;
        });
      });

      // Update internal data for consistency
      segment.updateInternalData();
    });
  }

  /**
   * Updates all text elements in the segment to have the specified speaker label
   *
   * @param speakerLabel The speaker label to assign to all elements
   */
  private updateAllTextElements(speakerLabel: string): void {
    this.segment.paragraphs.forEach(paragraph => {
      paragraph.elements.forEach(element => {
        // Update speaker label on all elements
        element.speakerLabel = speakerLabel;
        // Ensure parent references are maintained for all elements
        element.segmentId = this.segment.id;
        element.paragraphId = paragraph.id;
      });
    });

    // After updating all elements, ensure all parent-child relationships are updated
    this.segment.updateInternalData();
  }

  /**
   * Helper method to find a speaker label for a given datoSpeakerId
   * Searches the current speaker mappings
   *
   * @param datoSpeakerId The Dato speaker ID to look for
   * @returns The corresponding speaker label or undefined if not found
   */
  private findSpeakerLabelForDatoId(datoSpeakerId: string): string | undefined {
    const container = this.editor.getContainer();
    if (!container) return undefined;

    // Get speaker mappings from the editor
    const speakerMappings = this.editor.getSpeakerMappings();
    if (!speakerMappings) return undefined;

    // Search for a mapping that has this datoSpeakerId
    for (const [speakerLabel, mappedDatoId] of Object.entries(speakerMappings.mappings)) {
      if (mappedDatoId === datoSpeakerId) {
        return speakerLabel;
      }
    }

    return undefined;
  }

  private findDatoIdForSpeakerLabel(speakerLabel: string): string | undefined {
    const container = this.editor.getContainer();
    if (!container) return undefined;

    const speakerMappings = this.editor.getSpeakerMappings();
    if (!speakerMappings) return undefined;

    if (speakerMappings.mappings[speakerLabel]) {
      return speakerMappings.mappings[speakerLabel];
    }

    return undefined;
  }
}